import React, { useState, useEffect, useContext } from 'react'
import { Box, Button, Text, Stack } from "grommet"
import { CheckboxSelected } from 'grommet-icons'

import AgDataTable from '../common/AgDataTable'
import EnvContext from '../../context/EnvContext'
import AlertDetailedView from '../alert/AlertDetailedView'
import Spinner from '../common/Spinner'
import TriageUrlContext from '../../context/TriageUrlContext'

import axios from 'axios'

import { gql, useSubscription } from '@apollo/client';
import { useDefaultConf } from './TriageHeaders'

const TASK_QUERY = gql`
subscription($taskId: String!) {
    jobtask(where: {taskid: {_eq: $taskId}}) {
        taskid
        result
        status
      }
}
`

const PRICE_ENV_MAPPING = {
    "ext-prod-sim": "ext-prod-md-pp",
    "ext-prod-live": "ext-prod-md-pp",
    "ext-uat-cert": "ext-uat-md-sp",
    "int-dev-cert": "int-dev-md%",
    "int-stage-cert": "int-stage-md%",
    "int-sqe-cert": "int-sqe-md%",
    "int-dev-sim": "int-dev-md%"
}

const PSServiceNodes = gql`
subscription ($envName: String!) {
	service_node(
		where: {
			service: { serviceGroup: { group: { _eq: "price_server" } } }
			node: { environment: { name: { _like: $envName } } }
		}
	) {
		node_id: zookeeper(path: "custom_data.node_id")
		market_id: zookeeper(path: "custom_data.market_id")
		submarket_id: zookeeper(path: "custom_data.submarket_id")
		node {
			name
        }
        instance
	}
}
`

const TriagePSClientResult = ({ content }) => {

    const [result, setResult] = useState(false)

    const t3_url = useContext(TriageUrlContext)

    const { data: taskResult, loading: taskDataLoading } = useSubscription(
        TASK_QUERY, { variables: { taskId: content.taskid } }
    )

    const envSelected = useContext(EnvContext)
    const ps_svc_nodes = useSubscription(PSServiceNodes, {
        variables: {
            envName: PRICE_ENV_MAPPING[envSelected] ? PRICE_ENV_MAPPING[envSelected] : envSelected
        }
    })
    const axiosConf = useDefaultConf()

    useEffect(() => {
        if (!taskDataLoading && taskResult && taskResult.jobtask &&
            taskResult.jobtask.length > 0 && taskResult.jobtask[0].status !== 0
            && !(content.taskid.includes("MANUAL"))) {

            async function getStatusResponse() {
                const response = await axios(
                    t3_url + '/api/1/taskstatus/' + content.taskid, axiosConf
                )
                setResult(response.data)
            }

            getStatusResponse()
        }
    }, [axiosConf, content, taskDataLoading, taskResult, t3_url])

    // Get Node0,1,2,10 Info For Market
    const [node, setNode] = useState(false)
    const [instance, setInstance] = useState(false)

    const handleClick = (nodeId) => {
        setNode("-")
        setInstance("-")

        setNode(psInfo[nodeId])
        setInstance(psInfoInstance[nodeId])
    }

    const NodeHealthRenderer = params => {

        let nodes = JSON.parse(params.node.data.psNodeConnectionState)
        let selected = params.node.data.selectedNodeId

        return (
            <Box wrap={true} direction="row" gap="2px">
                {nodes && nodes.map(node => {
                    return (
                        <Stack anchor="right">

                            <Box key={node.nodeId}>
                                <Box
                                    width="130px"
                                    pad="xsmall"
                                    height="35px"
                                    direction="row"
                                    background={(node.connectionState === 'Ready' || node.connectionState === 'Connected') ? "#00873D" : "#B4130B"}
                                    data-tip={psInfo[node.nodeId]}
                                >
                                    <Button onClick={() => handleClick(node.nodeId)}>
                                        <Box>
                                            <Text
                                                size="xxsmall"
                                                color='white'
                                                truncate={true}
                                            >
                                                Node {node.nodeId} - {node.connectionState}
                                            </Text>
                                        </Box>
                                    </Button>
                                </Box>
                            </Box>
                            {selected === node.nodeId &&
                                <Box
                                    pad={{ horizontal: 'xsmall' }}
                                >
                                    <CheckboxSelected size="12px" color='white' />
                                </Box>
                            }
                        </Stack>
                    )
                }
                )
                }
            </Box>
        )
    }

    const tab_col = [
        {
            field: "node",
            headerName: "Node",
            minWidth: 150,
        },
        {
            field: "app",
            headerName: "App",
            minWidth: 150,
        },
        {
            field: "preferredNodeId",
            headerName: "Preferred NodeID",
            minWidth: 150,
        },
        {
            field: "selectedNodeId",
            headerName: "Selected Node",
            minWidth: 150,
            cellStyle: function (params) {
                if (params.value === -1) {
                    return { color: 'white', backgroundColor: '#B4130B' };
                }
            },
            sort: 'asc'
        },
        {
            field: "psNodeConnectionState",
            headerName: "PSNode Connection State",
            cellRenderer: NodeHealthRenderer,
            minWidth: 550,
        },
    ]

    let tab_data = []
    let marketId = ''

    if (result && result.results) {
        Object.keys(result.results).forEach(node => {
            Object.keys(result.results[node]).forEach(market => {
                marketId = market
                tab_data.push({
                    'node': node,
                    'uniqueId': node + market,
                    'preferredNodeId': result.results[node][market].preferredNodeId,
                    'selectedNodeId': result.results[node][market].selectedNodeId,
                    'psNodeConnectionState': JSON.stringify(result.results[node][market].nodes),
                    'app': result.results[node][market].app,
                })
            })

        })

    }

    let psInfo = {}
    let psInfoInstance = {}
    if (!ps_svc_nodes.loading && ps_svc_nodes.data && marketId) {
        marketId = marketId.split('.')[0]
        ps_svc_nodes.data.service_node.forEach(node => {
            if (node.market_id === parseInt(marketId)) {
                psInfo[node.node_id] = node.node.name
                psInfoInstance[node.node_id] = node.instance
            }
        })
    }

    let tab_data_node = []
    if (psInfo) {
        tab_data_node = [
            {
                'node0': psInfo && psInfo[0],
                'node1': psInfo && psInfo[1],
                'node2': psInfo && psInfo[2],
                'node10': psInfo && psInfo[10],
                'uniqueId': 1,
            }
        ]
    }

    const tab_col_node = [
        {
            field: "node0",
            headerName: "Node 0",
            minWidth: 150,
        },
        {
            field: "node1",
            headerName: "Node 1",
            minWidth: 150,
        },
        {
            field: "node2",
            headerName: "Node 2",
            minWidth: 150,
        },
        {
            field: "node10",
            headerName: "Node 10",
            minWidth: 150,
        }
    ]

    if (!psInfo || !psInfoInstance || !result || ps_svc_nodes.loading) {
        return <Spinner />
    }

    return (
        <Box margin={{ left: '20px', top: '0px', right: '20px', bottom: '20px' }}>

            <Text size="medium" weight='bold'>Market: {content.params.target && content.params.target.toUpperCase()}</Text>
            <Text size="medium" weight='bold'>SubMarket(s): {content.params && content.params.submarkets ? content.params.submarkets : "All"}</Text>

            <Box height="300px">
                <AgDataTable
                    tab_col={tab_col_node}
                    tab_data={tab_data_node}
                    tableName="PSClientDataNode"
                    disableButtons={true}
                />
            </Box>

            <AgDataTable
                tab_col={tab_col}
                tab_data={tab_data}
                tableName="PSClientData"
                disableButtons={true}
                tableHeight={700}
            />

            {node &&
                <AlertDetailedView
                    serviceInstanceSelected={instance}
                    nodeSelected={node}
                    nodeTypeSelected=""
                    serviceGroupSelected=""
                />
            }

        </Box>
    )
}

export default TriagePSClientResult